import { ICartData, ICartGroup, IWithListItem, IYouMayAlsoLikeItem } from '../interfaces';
import { getJSONFromHtml } from '../../../../domain/common';
import { restoreCartDataFromStorage, saveCartDataToStorage } from './cart-storage-provider';
import { IListItem } from '../../../list-page/interfaces/interfaces';

/**
 * apply computed prices on cart data
 */
const formatPrices = (cartData: ICartData) => {

    const pricesMap = new Map();
    if(cartData.priceData && cartData.priceData.cartItemPrices){
        for(let cartItemPrice of cartData.priceData.cartItemPrices) {
            pricesMap.set(cartItemPrice.cartItemGUID, cartItemPrice);
        }
    }

    for(let cartItem of cartData.cartItems){
        cartItem.priceData = pricesMap.has(cartItem.cartItemGUID) ? pricesMap.get(cartItem.cartItemGUID) : {};
    }

    return cartData;
};

/**
 * Server side shopping cart data can be received in format that is not convenient for some screens.
 * This format helper updates cart data, so it will be easier to use it in UI.
 */
export const formatCartData = (cartData: ICartData) => {
    if(!cartData) return cartData;

    // apply computed prices on cart data
    return formatPrices(cartData);
};

/**
 * get initial cart data from preloaded json
 * and handle session storage
 */
export const getInitialCartData = () => {

    const initialCartData = getJSONFromHtml(document.getElementById('preloaded-data'));
    const formattedCartData: ICartData = formatCartData(initialCartData);

    // if the cart is empty --> do nothing
    if(!formattedCartData || !formattedCartData.cartItems || formattedCartData.cartItems.length <= 0){
        return formattedCartData;
    }

    // try to get cart data from session storage
    const storageData = restoreCartDataFromStorage();

    if(!storageData){

        // if session storage doesn't exist --->
        // save preloaded data to session storage
        saveCartDataToStorage(formattedCartData);
    }
    else{
        // if session storage has address ---> restore it;
        // update the whole cart data
        const billingAddress = storageData.billingAddress ? storageData.billingAddress : formattedCartData?.billingAddress;
        const shippingAddress = storageData.shippingAddress ? storageData.shippingAddress : formattedCartData?.shippingAddress;

        formattedCartData.billingAddress = billingAddress;
        formattedCartData.shippingAddress = shippingAddress;
    }

    return formattedCartData;
};

export const splitCartDataItemPerGroups = (cartData: ICartData) : ICartGroup[] => {
    const groups: ICartGroup[] = [];

    if(!cartData || !cartData.cartGroups) return groups;

    const map = new Map();
    for(let cartItem of cartData.cartItems){
        const cartItems = map.has(cartItem.cartGroupID) ? map.get(cartItem.cartGroupID) : [];
        cartItems.push(cartItem);
        map.set(cartItem.cartGroupID, cartItems);
    }

    for(let group of cartData.cartGroups){
        const copy = {...group};
        copy.cartItems = map.has(group.cartGroupID) ? map.get(group.cartGroupID) : [];
        groups.push(copy);
    }

    return groups;
};

export const getSelectedCartItems = (cartData: ICartData) => {
    return cartData.cartItems.filter(cartItem => cartItem.isActive);
};

export const youMayAlsoLike2ListItem = (item: IYouMayAlsoLikeItem) => {

    const product: IListItem = {
        documentID: item.documentID,
        documentName: item.documentName,
        documentUrlPath: item.documentUrlPath,
        thumb: item.thumb,
        msrp: item.skU_MSRP,
        skuPrice: item.skuPrice,
        discount: item.discount,
        skuid: item.skuid,
        couponCode: item.couponCode,
        couponPrice: item.couponPrice,
        reviewsTotal: item.reviewsTotal,
        rating: item.rating,
        saleEndDate: item.saleEndDate,
        skuEnabled: true,

        badgeClass: '',
        badgeText: '',
        couponCampaignID: 0,
        departmentID: 0,
        documentNameCapitalLettersNum: 0,
        documentNameSmallLettersNum: 0,
        documentPageDescription: '',
        isPreorder: false,
        isProductOptions: false,
        isVariantsPriceSame: false,
        mainOrder: 0,
        nodeGUID: '',
        nodeID: 0,
        note: '',
        preview: '',
        skuNumber: '',
        tagline: '',
        variantsMinimalPrice: 0,
    };

    return product;
};

export const wishList2ListItem = (item: IWithListItem) => {

    const product: IListItem = {
        documentID: item.documentID,
        documentName: item.documentName,
        documentUrlPath: item.documentUrlPath,
        thumb: item.thumb,
        msrp: item.skU_MSRP,
        skuPrice: item.skuPrice,
        discount: item.discount,
        skuid: item.skuid,
        couponCode: item.couponCode,
        couponPrice: item.couponPrice,
        skuEnabled: true,

        rating: 0,
        reviewsTotal: 0,
        badgeClass: '',
        badgeText: '',
        couponCampaignID: 0,
        departmentID: 0,
        documentNameCapitalLettersNum: 0,
        documentNameSmallLettersNum: 0,
        documentPageDescription: '',
        isPreorder: false,
        isProductOptions: false,
        isVariantsPriceSame: false,
        mainOrder: 0,
        nodeGUID: '',
        nodeID: 0,
        note: '',
        preview: '',
        skuNumber: '',
        tagline: '',
        variantsMinimalPrice: 0
    };

    return product;
};